import React, { FC, useCallback, useMemo } from 'react';
import { Flex, NoSsr, TextV2 } from '@withjoy/joykit';
import { Info } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { addRendition } from '@shared/utils/photoRendition';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useFeatureValue } from '@shared/core/featureFlags';
import { GuestMediaGalleryComponentFragment, MagicTokenPermissions } from '@graphql/generated';
import { PhotoGallery as PhotoGalleryView } from '@apps/guest/routes/MediaCollection/routes/ViewAlbum/components/PhotoGallery/PhotoGallery';
import { body1ToParagraphOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { Photo } from '@apps/guest/routes/MediaCollection/routes/ViewAlbum/components/PhotoGallery/PhotoGallery.types';
import { useCustomPageTelemetry } from '../../Custom.telemetry';
import { useEventUserRole } from '@shared/components/AuthProvider';

interface PhotoGalleryProps {
  mediaGalleryComponentData: GuestMediaGalleryComponentFragment;
}

const PhotoGalleryHiddenState = () => {
  const { t } = useTranslation('guestSiteCustom');

  const isFontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';

  return (
    <Flex alignItems="flex-start" maxWidth={pxToRem(500)} padding={6} gap={6} borderRadius={3} border="1px solid" borderColor="info3" backgroundColor="white">
      <Info size={32} color="info5" />
      <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
        <TextV2 color="mono12" typographyVariant="body1" {...(!isFontPackExperimentEnabled && { fontFamily: 'Inter UI' })} lineHeight="21px">
          {t('photoGallery', 'emptyState', 'content')()}
        </TextV2>
      </GuestSiteTypographyOverride>
    </Flex>
  );
};

export const PhotoGallery: FC<PhotoGalleryProps> = props => {
  const { mediaGalleryComponentData } = props;

  const { eventInfo } = useEventInfo();
  const { isAdmin } = useEventUserRole();

  const telemetry = useCustomPageTelemetry();

  const photos: Photo[] = useMemo(
    () =>
      (mediaGalleryComponentData.mediaCollection?.media
        ?.map(media => {
          if (media.__typename === 'MediaPhoto') {
            return {
              mediaId: media.id,
              id: media.photo.id,
              src: addRendition({ url: media.photo.url, renditionSize: 'medium' }),
              width: media.photo.width,
              height: media.photo.height
            };
          }
          return null;
        })
        .filter(media => media !== null) as Photo[]) || [],
    [mediaGalleryComponentData.mediaCollection?.media]
  );

  const handlePhotoClick = useCallback(
    (mediaId: string, photoUrl: string) => {
      telemetry.photoCardClicked(eventInfo?.eventId || '', mediaGalleryComponentData.mediaCollection?.id || '', mediaId, photoUrl);
    },
    [eventInfo?.eventId, mediaGalleryComponentData.mediaCollection?.id, telemetry]
  );

  // TODO: Remove the !mediaGalleryComponentData.mediaCollection check once the BE fix the hidden album issue.
  // We will show the hidden UI state only for admins
  if (isAdmin && (!mediaGalleryComponentData.mediaCollection || !mediaGalleryComponentData.mediaCollection?.magicTokenPermissions?.includes(MagicTokenPermissions.view))) {
    return <PhotoGalleryHiddenState />;
  }

  return (
    <>
      {photos.length > 0 && (
        <NoSsr>
          <Flex width="100%" flexDirection="column">
            <PhotoGalleryView
              key={mediaGalleryComponentData.id}
              photos={photos}
              layout={photos.length > 8 ? 'masonry' : 'columns'}
              columns={3}
              spacing={2}
              padding={0}
              onPhotoClick={handlePhotoClick}
            />
          </Flex>
        </NoSsr>
      )}
    </>
  );
};

PhotoGallery.displayName = 'PhotoGallery';
